import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logger } from './utils/logger';
import { ScorecardPanel, OfferPanel } from './ScorecardOffer';
import { EmployerManifestBuilder, EmployerPackReadiness } from './RequirementManifest';
import RequisitionAIOverviews from './shell/RequisitionAIOverviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// Recruiter Cockpit — Kanban board with NBA chips and task side-panel
// ============================================================
export function RecruiterCockpit() {
  const [requisitions, setRequisitions] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [board, setBoard] = useState(null);
  const [showNewReq, setShowNewReq] = useState(false);
  const [showAddApp, setShowAddApp] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [showTasksPanel, setShowTasksPanel] = useState(false);
  const [showManifest, setShowManifest] = useState(false);

  const loadReqs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cockpit/requisitions`);
      setRequisitions(data);
      if (!selectedReq && data.length > 0) setSelectedReq(data[0].id);
    } catch (err) {
      logger.error('Failed to load requisitions', err);
    }
  }, [selectedReq]);

  const loadBoard = useCallback(async (reqId) => {
    if (!reqId) return;
    try {
      const { data } = await axios.get(`${API}/cockpit/board/${reqId}`);
      setBoard(data);
    } catch (err) {
      logger.error('Failed to load board', err);
    }
  }, []);

  const loadMyTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cockpit/tasks?mine=true&status=open`);
      setMyTasks(data);
    } catch (err) {
      logger.error('Failed to load tasks', err);
    }
  }, []);

  const loadCandidates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users?role=candidate`);
      setCandidates(data.filter(u => u.role === 'candidate'));
    } catch (err) {
      // Some recruiters won't have admin access; fall back to empty
      setCandidates([]);
    }
  }, []);

  useEffect(() => { loadReqs(); loadMyTasks(); }, [loadReqs, loadMyTasks]);
  useEffect(() => { if (selectedReq) loadBoard(selectedReq); }, [selectedReq, loadBoard]);

  const handleMoveStage = async (appId, newStage) => {
    try {
      await axios.put(`${API}/cockpit/applications/${appId}/move`, { new_stage: newStage });
      loadBoard(selectedReq);
    } catch (err) {
      alert('Failed to move: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="p-6" data-testid="recruiter-cockpit">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vault Hub</h1>
          <p className="text-sm text-gray-600 mt-1">Recruitment management workspace · Kanban pipeline · Next Best Action · Task allocation</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowTasksPanel(!showTasksPanel); loadMyTasks(); }}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 relative"
            data-testid="cockpit-my-tasks-button"
          >
            My Tasks
            {myTasks.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-600 text-white">{myTasks.length}</span>
            )}
          </button>
          <button
            onClick={() => setShowNewReq(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            data-testid="cockpit-new-requisition-button"
          >
            + New Requisition
          </button>
        </div>
      </div>

      <div className="mb-4 flex gap-2 flex-wrap">
        {requisitions.length === 0 ? (
          <div className="text-sm text-gray-500 bg-white p-4 rounded-lg w-full">No requisitions yet. Create one to start building your pipeline.</div>
        ) : (
          requisitions.map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedReq(r.id)}
              className={`px-3 py-2 rounded-lg text-sm transition ${selectedReq === r.id ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
              data-testid={`cockpit-req-tab-${r.id}`}
            >
              <span className="font-semibold">{r.title}</span>
              <span className="ml-2 text-xs opacity-80">{r.country} • {r.legal_entity}</span>
              {r.required_approvals?.length > 0 && (
                <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${selectedReq === r.id ? 'bg-white text-indigo-700' : 'bg-amber-100 text-amber-700'}`} title="Required approvals">⚠️ {r.required_approvals.length}</span>
              )}
            </button>
          ))
        )}
      </div>

      {board && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-4 flex justify-between items-center">
          <div>
            <h2 className="font-semibold text-gray-900">{board.requisition.title}</h2>
            <p className="text-xs text-gray-600 mt-1">
              {board.requisition.country} • {board.requisition.legal_entity} • {board.requisition.business_line || '—'} • {board.requisition.seniority_band || '—'}
            </p>
            {board.requisition.matched_rules?.length > 0 && (
              <p className="text-xs text-amber-700 mt-1">⚠️ Governed by: {board.requisition.matched_rules.join(', ')}</p>
            )}
          </div>
          <button
            onClick={() => { loadCandidates(); setShowAddApp(true); }}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
            data-testid="cockpit-add-applicant-button"
          >
            + Add Applicant
          </button>
          <button
            onClick={() => setShowManifest((v) => !v)}
            className="ml-2 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
            data-testid="cockpit-manifest-button"
          >
            🛡️ {showManifest ? 'Hide' : 'Manage'} pack manifest
          </button>
        </div>
      )}

      {board && showManifest && (
        <div className="mb-4" data-testid="cockpit-manifest-panel">
          <EmployerManifestBuilder requisitionId={selectedReq} onSaved={() => setShowManifest(false)} />
        </div>
      )}

      {board && <RequisitionAIOverviews requisitionId={selectedReq} />}

      {board && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="cockpit-kanban">
          {board.columns.map(col => {
            // Hoisted: other-stage shortcuts are the same for every card in this column
            const otherStages = board.columns.filter(c => c.stage !== col.stage).slice(0, 2);
            return (
            <div key={col.stage} className="flex-shrink-0 w-72 bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid={`cockpit-column-${col.stage}`}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm text-gray-700">{col.stage}</h3>
                <div className="flex gap-1 items-center">
                  {col.sla_days && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">SLA {col.sla_days}d</span>}
                  <span className="text-xs px-2 py-0.5 rounded bg-white text-gray-600">{col.applications.length}</span>
                </div>
              </div>
              <div className="space-y-2">
                {col.applications.map(app => (
                  <div
                    key={app.id}
                    onClick={() => setSelectedApp(app)}
                    className="bg-white p-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition"
                    data-testid={`cockpit-app-card-${app.id}`}
                  >
                    <p className="font-semibold text-sm text-gray-900">{app.candidate_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Source: {app.source || 'manual'}</p>
                    {app.nba && (
                      <div className={`mt-2 px-2 py-1 rounded text-xs flex items-start gap-1 ${
                        app.nba.severity === 'high' ? 'bg-red-100 text-red-800' :
                        app.nba.severity === 'medium' ? 'bg-amber-100 text-amber-800' :
                        app.nba.severity === 'low' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-700'
                      }`} data-testid={`cockpit-nba-${app.id}`}>
                        <span>🎯</span><span>{app.nba.label}</span>
                      </div>
                    )}
                    {app.open_tasks > 0 && (
                      <div className="mt-2 text-xs text-gray-500">📋 {app.open_tasks} open task{app.open_tasks > 1 ? 's' : ''}</div>
                    )}
                    {app.compliance_readiness && (() => {
                      const cr = app.compliance_readiness;
                      const tierColor =
                        cr.tier === 'Complete' ? 'bg-emerald-100 text-emerald-800' :
                        cr.tier === 'Strong'   ? 'bg-blue-100 text-blue-800' :
                        cr.tier === 'Building' ? 'bg-amber-100 text-amber-800' :
                        cr.tier === 'Started'  ? 'bg-orange-100 text-orange-700' :
                                                  'bg-gray-100 text-gray-600';
                      return (
                        <div
                          className={`mt-2 px-2 py-1 rounded text-xs flex items-center gap-1.5 ${tierColor}`}
                          title={`Compliance pack readiness: ${cr.categories_filled} of ${cr.categories_total} banking categories covered. The candidate has documents on file but only this aggregate signal is shared with you.`}
                          data-testid={`cockpit-readiness-${app.id}`}
                        >
                          <span>🛡️</span>
                          <span className="font-medium">Compliance: {cr.tier}</span>
                          <span className="opacity-70">({cr.categories_filled}/{cr.categories_total})</span>
                        </div>
                      );
                    })()}
                    <div className="mt-2"><EmployerPackReadiness applicationId={app.id} compact /></div>
                    <div className="mt-2 flex gap-1">
                      {otherStages.map(c => (
                        <button
                          key={c.stage}
                          onClick={(e) => { e.stopPropagation(); handleMoveStage(app.id, c.stage); }}
                          className="text-xs px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                          → {c.stage}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {col.applications.length === 0 && (
                  <div className="text-xs text-gray-400 text-center py-4">No candidates</div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showNewReq && (
        <NewRequisitionModal onClose={() => setShowNewReq(false)} onCreated={() => { setShowNewReq(false); loadReqs(); }} />
      )}

      {showAddApp && board && (
        <AddApplicantModal
          requisitionId={selectedReq}
          candidates={candidates}
          onClose={() => setShowAddApp(false)}
          onCreated={() => { setShowAddApp(false); loadBoard(selectedReq); }}
        />
      )}

      {selectedApp && (
        <ApplicationDetailPanel
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onUpdate={() => loadBoard(selectedReq)}
        />
      )}

      {showTasksPanel && (
        <MyTasksPanel tasks={myTasks} onClose={() => setShowTasksPanel(false)} onUpdate={loadMyTasks} />
      )}
    </div>
  );
}

function NewRequisitionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    country: 'GB',
    legal_entity: '',
    business_line: '',
    seniority_band: 'VP',
    department: '',
    hiring_manager_email: '',
    headcount: 1
  });
  const [submitting, setSubmitting] = useState(false);
  const [evaluation, setEvaluation] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/cockpit/requisitions`, form);
      setEvaluation(data);
      setTimeout(() => onCreated(), 800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        alert('Blocked by rules: ' + JSON.stringify(detail.matched_rules?.map(r => r.name) || []));
      } else {
        alert('Failed: ' + (detail || err.message));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-req-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">New Requisition</h2>
          <p className="text-sm text-gray-500">Rules engine will evaluate access and approval requirements automatically.</p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="new-req-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Country (ISO)</label>
              <select value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="new-req-country">
                <option value="GB">GB - United Kingdom</option>
                <option value="US">US - United States</option>
                <option value="DE">DE - Germany</option>
                <option value="FR">FR - France</option>
                <option value="ES">ES - Spain</option>
                <option value="IT">IT - Italy</option>
                <option value="NL">NL - Netherlands</option>
                <option value="SG">SG - Singapore</option>
                <option value="HK">HK - Hong Kong</option>
                <option value="JP">JP - Japan</option>
                <option value="AE">AE - UAE</option>
                <option value="CH">CH - Switzerland</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Legal Entity</label>
              <input required value={form.legal_entity} onChange={e => setForm({...form, legal_entity: e.target.value})} placeholder="e.g. HSBC Bank plc" className="w-full px-3 py-2 border rounded" data-testid="new-req-entity" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Business Line</label>
              <select value={form.business_line} onChange={e => setForm({...form, business_line: e.target.value})} className="w-full px-3 py-2 border rounded">
                <option value="">— select —</option>
                <option>Investment Banking</option>
                <option>Markets</option>
                <option>Retail Banking</option>
                <option>Wealth Management</option>
                <option>Asset Management</option>
                <option>Corporate Banking</option>
                <option>Operations</option>
                <option>Risk & Compliance</option>
                <option>Technology</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Seniority Band</label>
              <select value={form.seniority_band} onChange={e => setForm({...form, seniority_band: e.target.value})} className="w-full px-3 py-2 border rounded">
                {['Analyst','Associate','VP','Director','MD','Other'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Department</label>
              <input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Headcount</label>
              <input type="number" min="1" value={form.headcount} onChange={e => setForm({...form, headcount: parseInt(e.target.value)||1})} className="w-full px-3 py-2 border rounded" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hiring Manager Email</label>
            <input type="email" value={form.hiring_manager_email} onChange={e => setForm({...form, hiring_manager_email: e.target.value})} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded" />
          </div>

          {evaluation && (
            <div className="bg-green-50 border border-green-200 p-3 rounded text-sm">
              <p className="text-green-800 font-medium">✅ Created!</p>
              {evaluation.required_approvals?.length > 0 && (
                <p className="text-amber-700 text-xs mt-1">Approval needed: {evaluation.required_approvals.map(a => a.approver_role).join(', ')}</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-50" data-testid="new-req-submit">
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddApplicantModal({ requisitionId, candidates, onClose, onCreated }) {
  const [candidateId, setCandidateId] = useState('');
  const [source, setSource] = useState('manual');
  const [submitting, setSubmitting] = useState(false);
  const [allCandidates, setAllCandidates] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cockpit/candidates`);
        setAllCandidates(data.map(c => ({ id: c.id, name: c.full_name, email: c.email })));
      } catch (err) {
        setLoadError(err.response?.data?.detail || err.message);
        setAllCandidates([]);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!candidateId) return alert('Select a candidate');
    setSubmitting(true);
    try {
      await axios.post(`${API}/cockpit/applications`, {
        requisition_id: requisitionId,
        candidate_id: candidateId,
        source
      });
      onCreated();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Add Applicant</h2>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {loadError && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">Couldn't load candidates: {loadError}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">Candidate</label>
            <select required value={candidateId} onChange={e => setCandidateId(e.target.value)} className="w-full px-3 py-2 border rounded" data-testid="add-applicant-select">
              <option value="">— select —</option>
              {allCandidates.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Source</label>
            <select value={source} onChange={e => setSource(e.target.value)} className="w-full px-3 py-2 border rounded">
              <option value="manual">Manual</option>
              <option value="referral">Referral</option>
              <option value="linkedin">LinkedIn</option>
              <option value="indeed">Indeed</option>
              <option value="career_site">Career Site</option>
              <option value="agency">Agency</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm disabled:opacity-50">
              {submitting ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApplicationDetailPanel({ application, onClose, onUpdate }) {
  const [tasks, setTasks] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'medium', due_date: '', description: '' });

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cockpit/tasks?application_id=${application.id}`);
      setTasks(data);
    } catch (err) { logger.error(err); }
  }, [application.id]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const addTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cockpit/tasks`, { ...newTask, application_id: application.id });
      setShowAddTask(false);
      setNewTask({ title: '', priority: 'medium', due_date: '', description: '' });
      loadTasks();
      onUpdate();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const completeTask = async (taskId) => {
    await axios.put(`${API}/cockpit/tasks/${taskId}`, { status: 'done' });
    loadTasks();
    onUpdate();
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto" data-testid="application-detail-panel">
      <div className="p-5 border-b flex justify-between items-start">
        <div>
          <h2 className="text-lg font-semibold">{application.candidate_name}</h2>
          <p className="text-xs text-gray-500 mt-1">Stage: <span className="font-medium">{application.stage}</span></p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>

      {application.nba && (
        <div className="m-5 p-3 rounded bg-indigo-50 border border-indigo-200">
          <p className="text-xs font-semibold text-indigo-900">🎯 Next Best Action</p>
          <p className="text-sm text-indigo-800 mt-1">{application.nba.label}</p>
        </div>
      )}

      <div className="p-5 border-t">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-sm">Tasks ({tasks.length})</h3>
          <button onClick={() => setShowAddTask(!showAddTask)} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">+ Task</button>
        </div>

        {showAddTask && (
          <form onSubmit={addTask} className="space-y-2 mb-3 p-3 bg-gray-50 rounded">
            <input required placeholder="Title" value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} className="w-full px-2 py-1 border rounded text-sm" data-testid="new-task-title" />
            <div className="grid grid-cols-2 gap-2">
              <select value={newTask.priority} onChange={e => setNewTask({...newTask, priority: e.target.value})} className="px-2 py-1 border rounded text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
              <input type="date" value={newTask.due_date} onChange={e => setNewTask({...newTask, due_date: e.target.value})} className="px-2 py-1 border rounded text-sm" />
            </div>
            <button type="submit" className="px-3 py-1 bg-indigo-600 text-white rounded text-xs">Save Task</button>
          </form>
        )}

        <div className="space-y-2">
          {tasks.map(t => (
            <div key={t.id} className={`p-2 rounded border text-sm ${t.status === 'done' ? 'bg-gray-50 opacity-60 line-through' : 'bg-white'}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1">
                  <p className="font-medium">{t.title}</p>
                  {t.due_date && <p className="text-xs text-gray-500">Due {t.due_date}</p>}
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    t.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    t.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                  }`}>{t.priority}</span>
                  {t.status !== 'done' && <button onClick={() => completeTask(t.id)} className="text-xs px-2 py-0.5 bg-green-600 text-white rounded">Done</button>}
                </div>
              </div>
            </div>
          ))}
          {tasks.length === 0 && <p className="text-xs text-gray-500 text-center py-3">No tasks yet.</p>}
        </div>
      </div>

      <div className="p-5 border-t">
        <h3 className="font-semibold text-sm mb-2">Stage History</h3>
        <div className="space-y-1">
          {(application.stage_history || []).map((h, idx) => (
            <div key={`${h.entered_at}-${idx}`} className="text-xs text-gray-600">
              <span className="font-medium">{h.stage}</span> — {new Date(h.entered_at).toLocaleString()} by {h.by}
              {h.from_stage && <span className="text-gray-400"> (from {h.from_stage})</span>}
            </div>
          ))}
        </div>
      </div>

      <ScorecardPanel applicationId={application.id} />
      <OfferPanel applicationId={application.id} />
    </div>
  );
}

function MyTasksPanel({ tasks, onClose, onUpdate }) {
  const completeTask = async (taskId) => {
    await axios.put(`${API}/cockpit/tasks/${taskId}`, { status: 'done' });
    onUpdate();
  };
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto" data-testid="my-tasks-panel">
      <div className="p-5 border-b flex justify-between items-start">
        <h2 className="text-lg font-semibold">My Open Tasks ({tasks.length})</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>
      <div className="p-5 space-y-2">
        {tasks.map(t => (
          <div key={t.id} className="p-3 bg-gray-50 rounded border">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="font-medium text-sm">{t.title}</p>
                {t.description && <p className="text-xs text-gray-600 mt-1">{t.description}</p>}
                {t.due_date && <p className="text-xs text-gray-500 mt-1">Due {t.due_date}</p>}
              </div>
              <div className="flex flex-col gap-1 items-end ml-2">
                <span className={`text-xs px-2 py-0.5 rounded ${
                  t.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                  t.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                }`}>{t.priority}</span>
                <button onClick={() => completeTask(t.id)} className="text-xs px-2 py-0.5 bg-green-600 text-white rounded">Done</button>
              </div>
            </div>
          </div>
        ))}
        {tasks.length === 0 && <p className="text-sm text-gray-500 text-center py-6">All caught up! 🎉</p>}
      </div>
    </div>
  );
}


// ============================================================
// Rules Engine Admin UI
// ============================================================
export function RulesEngineView() {
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({ rule_types: [], operators: [], actions: [], fields: [] });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: rs }, { data: m }] = await Promise.all([
        axios.get(`${API}/admin/rules`),
        axios.get(`${API}/admin/rules/types`)
      ]);
      setRules(rs);
      setMeta(m);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await axios.delete(`${API}/admin/rules/${id}`);
    load();
  };

  const toggleActive = async (rule) => {
    await axios.put(`${API}/admin/rules/${rule.id}`, { active: !rule.active });
    load();
  };

  return (
    <div data-testid="rules-engine-view">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Rules Engine</h2>
          <p className="text-sm text-gray-500 mt-1">Customisable rules govern requisitions, AI features, approvals, data residency, and routing.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700" data-testid="rules-new-button">+ New Rule</button>
      </div>

      <div className="space-y-2">
        {rules.map(r => (
          <div key={r.id} className="bg-white p-4 rounded-lg shadow border-l-4 border-indigo-500" data-testid={`rule-item-${r.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold">{r.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.active ? 'active' : 'inactive'}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">{r.rule_type}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    r.action === 'disallow' ? 'bg-red-100 text-red-700' :
                    r.action === 'require_approval' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{r.action}</span>
                  <span className="text-xs text-gray-500">priority {r.priority}</span>
                </div>
                {r.description && <p className="text-sm text-gray-600">{r.description}</p>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.scope_countries?.map(c => <span key={c} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">🌐 {c}</span>)}
                  {r.scope_legal_entities?.map(e => <span key={e} className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">🏢 {e}</span>)}
                  {r.scope_business_lines?.map(b => <span key={b} className="text-xs px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">{b}</span>)}
                  {r.scope_seniority_bands?.map(s => <span key={s} className="text-xs px-1.5 py-0.5 rounded bg-pink-50 text-pink-700">{s}</span>)}
                </div>
                {r.conditions?.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2 font-mono">
                    IF {r.conditions.map(c => `${c.field} ${c.operator} ${JSON.stringify(c.value)}`).join(` ${r.condition_logic} `)}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1 ml-3">
                <button onClick={() => { setEditing(r); setShowForm(true); }} className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded">Edit</button>
                <button onClick={() => toggleActive(r)} className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded">{r.active ? 'Disable' : 'Enable'}</button>
                <button onClick={() => handleDelete(r.id)} className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {rules.length === 0 && <div className="bg-white p-6 rounded-lg text-center text-gray-500">No rules defined yet.</div>}
      </div>

      {showForm && (
        <RuleForm rule={editing} meta={meta} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function RuleForm({ rule, meta, onClose, onSaved }) {
  const [form, setForm] = useState(rule || {
    name: '', description: '', rule_type: 'requisition_access',
    scope_countries: [], scope_legal_entities: [], scope_business_lines: [], scope_seniority_bands: [], scope_roles: [],
    conditions: [], condition_logic: 'AND',
    action: 'allow', action_params: {},
    priority: 100, active: true
  });

  const [scopeCountriesText, setScopeCountriesText] = useState((form.scope_countries || []).join(', '));
  const [scopeEntitiesText, setScopeEntitiesText] = useState((form.scope_legal_entities || []).join(', '));
  const [scopeBLText, setScopeBLText] = useState((form.scope_business_lines || []).join(', '));
  const [scopeSeniorityText, setScopeSeniorityText] = useState((form.scope_seniority_bands || []).join(', '));

  const parseList = (s) => s.split(',').map(x => x.trim()).filter(Boolean);

  const addCondition = () => setForm({...form, conditions: [...(form.conditions||[]), {_key: `c-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, field: meta.fields[0] || 'requisition.country', operator: 'equals', value: ''}]});
  const updateCondition = (idx, key, val) => {
    const next = [...(form.conditions||[])];
    next[idx] = {...next[idx], [key]: val};
    setForm({...form, conditions: next});
  };
  const removeCondition = (idx) => setForm({...form, conditions: form.conditions.filter((_, i) => i !== idx)});

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      scope_countries: parseList(scopeCountriesText),
      scope_legal_entities: parseList(scopeEntitiesText),
      scope_business_lines: parseList(scopeBLText),
      scope_seniority_bands: parseList(scopeSeniorityText)
    };
    try {
      if (rule) {
        await axios.put(`${API}/admin/rules/${rule.id}`, payload);
      } else {
        await axios.post(`${API}/admin/rules`, payload);
      }
      onSaved();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b">
          <h2 className="text-xl font-semibold">{rule ? 'Edit Rule' : 'New Rule'}</h2>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="rule-name" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea rows="2" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rule Type</label>
              <select value={form.rule_type} onChange={e => setForm({...form, rule_type: e.target.value})} className="w-full px-3 py-2 border rounded">
                {meta.rule_types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Action</label>
              <select value={form.action} onChange={e => setForm({...form, action: e.target.value})} className="w-full px-3 py-2 border rounded">
                {meta.actions.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
            {form.action === 'require_approval' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Approver Role</label>
                <input value={form.action_params?.approver_role || ''} onChange={e => setForm({...form, action_params: {...form.action_params, approver_role: e.target.value}})} placeholder="e.g. compliance_officer" className="w-full px-3 py-2 border rounded" />
              </div>
            )}
            {form.action === 'route_to' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Route To (team or user)</label>
                <input value={form.action_params?.route_to || ''} onChange={e => setForm({...form, action_params: {...form.action_params, route_to: e.target.value}})} className="w-full px-3 py-2 border rounded" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Priority (lower = first)</label>
              <input type="number" value={form.priority} onChange={e => setForm({...form, priority: parseInt(e.target.value) || 100})} className="w-full px-3 py-2 border rounded" />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} /> Active
              </label>
            </div>
          </div>

          <fieldset className="border p-3 rounded">
            <legend className="text-sm font-medium px-1">Scope (empty = applies to all)</legend>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Countries (ISO, comma-separated)</label>
                <input value={scopeCountriesText} onChange={e => setScopeCountriesText(e.target.value)} placeholder="GB, DE, US" className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Legal Entities</label>
                <input value={scopeEntitiesText} onChange={e => setScopeEntitiesText(e.target.value)} placeholder="HSBC Holdings plc, ..." className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Business Lines</label>
                <input value={scopeBLText} onChange={e => setScopeBLText(e.target.value)} placeholder="Investment Banking, Markets" className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Seniority Bands</label>
                <input value={scopeSeniorityText} onChange={e => setScopeSeniorityText(e.target.value)} placeholder="MD, Director" className="w-full px-2 py-1 border rounded text-sm" />
              </div>
            </div>
          </fieldset>

          <fieldset className="border p-3 rounded">
            <legend className="text-sm font-medium px-1 flex items-center gap-2">
              Conditions
              <select value={form.condition_logic} onChange={e => setForm({...form, condition_logic: e.target.value})} className="text-xs px-1 py-0.5 border rounded">
                <option value="AND">ALL must match (AND)</option>
                <option value="OR">ANY may match (OR)</option>
              </select>
            </legend>
            {(form.conditions || []).map((c, idx) => (
              <div key={c._key || `cond-${idx}`} className="flex gap-2 items-center mb-2">
                <select value={c.field} onChange={e => updateCondition(idx, 'field', e.target.value)} className="px-2 py-1 border rounded text-sm flex-1">
                  {meta.fields.map(f => <option key={f}>{f}</option>)}
                </select>
                <select value={c.operator} onChange={e => updateCondition(idx, 'operator', e.target.value)} className="px-2 py-1 border rounded text-sm">
                  {meta.operators.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                <input value={typeof c.value === 'string' ? c.value : JSON.stringify(c.value)} onChange={e => {
                  let v = e.target.value;
                  // Allow JSON arrays for "in"/"not_in"
                  if ((c.operator === 'in' || c.operator === 'not_in') && v.startsWith('[')) {
                    try { v = JSON.parse(v); } catch (parseErr) {
                      // Invalid JSON during typing — keep raw string until user finishes input
                    }
                  }
                  updateCondition(idx, 'value', v);
                }} placeholder="value" className="px-2 py-1 border rounded text-sm flex-1" />
                <button type="button" onClick={() => removeCondition(idx)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">×</button>
              </div>
            ))}
            <button type="button" onClick={addCondition} className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded">+ Add Condition</button>
          </fieldset>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded text-sm" data-testid="rule-save">Save Rule</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============================================================
// Integrations Admin UI
// ============================================================
export function IntegrationsView() {
  const [integrations, setIntegrations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [configuring, setConfiguring] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/integrations`);
      setIntegrations(data.integrations);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(integrations.map(i => i.category)))],
    [integrations]
  );
  const filtered = useMemo(
    () => filter === 'all' ? integrations : integrations.filter(i => i.category === filter),
    [integrations, filter]
  );

  const categoryLabels = {
    job_board: 'Job Boards',
    job_aggregator: 'Job Aggregators',
    comms: 'Comms (Teams/Slack)',
    email: 'Email',
    calendar: 'Calendar',
    assessment: 'Assessments',
    background_check: 'Background Checks',
    identity: 'Identity Verification',
    esign: 'E-Signature',
    sso: 'Single Sign-On',
    registry: 'Company Registries',
    ai: 'AI Providers',
    api_management: 'API Management'
  };

  return (
    <div data-testid="integrations-view">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Integrations</h2>
          <p className="text-sm text-gray-500 mt-1">Connect Vault Hire to job boards, comms, assessments, identity providers, and more.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {categories.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded text-xs transition ${filter === c ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
            data-testid={`integrations-filter-${c}`}
          >
            {c === 'all' ? 'All' : (categoryLabels[c] || c)}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(i => (
          <div key={i.id} className="bg-white rounded-lg shadow p-4 border border-gray-200" data-testid={`integration-card-${i.id}`}>
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-sm">{i.name}</h3>
              {i.stage === 'connected' ? (
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">✓ Connected</span>
              ) : i.stage === 'coming_soon' ? (
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">Coming Soon</span>
              ) : i.configured && i.enabled ? (
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">✓ Enabled</span>
              ) : i.configured ? (
                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Configured (Off)</span>
              ) : (
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Available</span>
              )}
            </div>
            <p className="text-xs text-gray-600 mb-3">{i.description}</p>
            <div className="text-xs text-gray-400 mb-3">{categoryLabels[i.category] || i.category}</div>
            {i.stage !== 'coming_soon' && i.stage !== 'connected' && (
              <button
                onClick={() => setConfiguring(i)}
                className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                data-testid={`integration-configure-${i.id}`}
              >
                {i.configured ? 'Update' : 'Configure'}
              </button>
            )}
            {i.stage === 'connected' && (
              <p className="text-xs text-green-600">Live in production</p>
            )}
          </div>
        ))}
      </div>

      {configuring && (
        <IntegrationConfigureModal integration={configuring} onClose={() => setConfiguring(null)} onSaved={() => { setConfiguring(null); load(); }} />
      )}
    </div>
  );
}

function IntegrationConfigureModal({ integration, onClose, onSaved }) {
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [enabled, setEnabled] = useState(true);

  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/integrations/${integration.id}`, {
        api_key: apiKey || null,
        api_secret: apiSecret || null,
        enabled,
        config: {}
      });
      onSaved();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">Configure {integration.name}</h2>
          <p className="text-xs text-gray-500 mt-1">{integration.description}</p>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">API Key</label>
            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} className="w-full px-3 py-2 border rounded font-mono text-sm" placeholder="Paste API key..." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">API Secret (if required)</label>
            <input type="password" value={apiSecret} onChange={e => setApiSecret(e.target.value)} className="w-full px-3 py-2 border rounded font-mono text-sm" placeholder="Optional" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Enabled
          </label>
          <p className="text-xs text-amber-700">⚠️ Keys are stored on the server. Production deployments must encrypt at rest.</p>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============================================================
// AI Governance Admin UI
// ============================================================
export function AIGovernanceView() {
  const [toggles, setToggles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-governance`);
      setToggles(data.toggles);
      setFeatures(data.features);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeToggle = async (t) => {
    // Toggle to enabled to remove a "disabled" override, since we don't have a delete endpoint
    await axios.put(`${API}/admin/ai-governance`, { ...t, enabled: !t.enabled });
    load();
  };

  return (
    <div data-testid="ai-governance-view">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-semibold">AI Governance</h2>
          <p className="text-sm text-gray-500 mt-1">Switch AI features on/off per legal entity and country. Useful for GDPR-strict regions or EU AI Act compliance.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700" data-testid="ai-governance-new">+ Add Override</button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase">Legal Entity</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase">Country</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase">Feature</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase">Status</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase">Updated</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {toggles.map((t) => (
              <tr key={`${t.legal_entity}-${t.country || '_any'}-${t.feature}`} className="border-t">
                <td className="px-4 py-2">{t.legal_entity}</td>
                <td className="px-4 py-2">{t.country || '—'}</td>
                <td className="px-4 py-2">{features.find(f => f.id === t.feature)?.label || t.feature}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${t.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {t.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{new Date(t.updated_at).toLocaleDateString()}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => removeToggle(t)} className="text-xs px-2 py-1 bg-gray-100 rounded">Flip</button>
                </td>
              </tr>
            ))}
            {toggles.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-500 text-sm">No overrides set — all AI features default to enabled.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <AIGovernanceForm features={features} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function AIGovernanceForm({ features, onClose, onSaved }) {
  const [form, setForm] = useState({ legal_entity: '', country: '', feature: features[0]?.id || 'ai_profile', enabled: false });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/ai-governance`, { ...form, country: form.country || null });
      onSaved();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b"><h2 className="text-lg font-semibold">New AI Toggle</h2></div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Legal Entity (or _global)</label>
            <input required value={form.legal_entity} onChange={e => setForm({...form, legal_entity: e.target.value})} placeholder="e.g. HSBC Holdings plc" className="w-full px-3 py-2 border rounded" data-testid="ai-toggle-entity" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Country (optional ISO code)</label>
            <input value={form.country} onChange={e => setForm({...form, country: e.target.value})} placeholder="e.g. DE (blank = all)" className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Feature</label>
            <select value={form.feature} onChange={e => setForm({...form, feature: e.target.value})} className="w-full px-3 py-2 border rounded">
              {features.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={e => setForm({...form, enabled: e.target.checked})} /> Enabled
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============================================================
// Compliance Sandbox — interactive rule simulator
// ============================================================
export function ComplianceSandboxView() {
  const [ctx, setCtx] = useState({
    country: 'GB',
    legal_entity: '',
    business_line: '',
    seniority_band: 'VP',
    department: '',
    user_role: 'employer'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const loadScenarios = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/rules/scenarios`);
      setScenarios(data);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const runSimulation = async () => {
    setLoading(true);
    try {
      const payload = {
        context: {
          requisition: {
            country: ctx.country,
            legal_entity: ctx.legal_entity,
            business_line: ctx.business_line,
            seniority_band: ctx.seniority_band,
            department: ctx.department
          },
          user: { role: ctx.user_role }
        }
      };
      const { data } = await axios.post(`${API}/admin/rules/simulate`, payload);
      setResult(data);
    } catch (err) {
      alert('Simulation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadScenario = (s) => {
    const req = s.context.requisition || {};
    const u = s.context.user || {};
    setCtx({
      country: req.country || 'GB',
      legal_entity: req.legal_entity || '',
      business_line: req.business_line || '',
      seniority_band: req.seniority_band || 'VP',
      department: req.department || '',
      user_role: u.role || 'employer'
    });
    setResult(null);
  };

  const deleteScenario = async (id) => {
    if (!window.confirm('Delete scenario?')) return;
    await axios.delete(`${API}/admin/rules/scenarios/${id}`);
    loadScenarios();
  };

  return (
    <div data-testid="compliance-sandbox-view">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Compliance Sandbox</h2>
          <p className="text-sm text-gray-500 mt-1">Test how the rules engine evaluates a hypothetical requisition. Use this to validate policy changes <em>before</em> they go live.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: Context builder */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-5 mb-4">
            <h3 className="font-semibold mb-3 text-sm">Scenario Context</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Country (ISO)</label>
                <select value={ctx.country} onChange={e => setCtx({...ctx, country: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" data-testid="sandbox-country">
                  {['GB','US','DE','FR','ES','IT','NL','BE','LU','CH','SG','HK','JP','AE','IE','PT','AT','PL','SE'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Legal Entity</label>
                <input value={ctx.legal_entity} onChange={e => setCtx({...ctx, legal_entity: e.target.value})} placeholder="e.g. HSBC Bank plc" className="w-full px-3 py-2 border rounded text-sm" data-testid="sandbox-entity" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Business Line</label>
                <select value={ctx.business_line} onChange={e => setCtx({...ctx, business_line: e.target.value})} className="w-full px-3 py-2 border rounded text-sm">
                  <option value="">— any —</option>
                  <option>Investment Banking</option>
                  <option>Markets</option>
                  <option>Retail Banking</option>
                  <option>Wealth Management</option>
                  <option>Asset Management</option>
                  <option>Corporate Banking</option>
                  <option>Operations</option>
                  <option>Risk & Compliance</option>
                  <option>Technology</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Seniority Band</label>
                <select value={ctx.seniority_band} onChange={e => setCtx({...ctx, seniority_band: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" data-testid="sandbox-seniority">
                  {['Analyst','Associate','VP','Director','MD'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Department (optional)</label>
                <input value={ctx.department} onChange={e => setCtx({...ctx, department: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Acting User Role</label>
                <select value={ctx.user_role} onChange={e => setCtx({...ctx, user_role: e.target.value})} className="w-full px-3 py-2 border rounded text-sm">
                  <option value="employer">Recruiter (employer)</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                  <option value="hiring_manager">Hiring Manager</option>
                </select>
              </div>
              <button onClick={runSimulation} disabled={loading} className="w-full px-3 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-50" data-testid="sandbox-run">
                {loading ? 'Simulating...' : '▶ Run Simulation'}
              </button>
              {result && (
                <button onClick={() => setShowSaveModal(true)} className="w-full px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs">
                  💾 Save as Scenario
                </button>
              )}
            </div>
          </div>

          {/* Saved scenarios */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="font-semibold mb-3 text-sm">Saved Scenarios</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {scenarios.map(s => (
                <div key={s.id} className="border rounded p-2 text-xs" data-testid={`sandbox-scenario-${s.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold">{s.name}</p>
                      {s.description && <p className="text-gray-500">{s.description}</p>}
                      <p className="text-gray-400 mt-1">
                        {s.context?.requisition?.country} / {s.context?.requisition?.seniority_band || '—'} / {s.context?.requisition?.business_line || 'any'}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => loadScenario(s)} className="px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded">Load</button>
                      <button onClick={() => deleteScenario(s.id)} className="px-2 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 rounded">×</button>
                    </div>
                  </div>
                </div>
              ))}
              {scenarios.length === 0 && <p className="text-xs text-gray-400">No saved scenarios yet.</p>}
            </div>
          </div>
        </div>

        {/* Right: Simulation result */}
        <div className="lg:col-span-2">
          {!result && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
              <p className="text-sm">Build a scenario on the left and click <strong>Run Simulation</strong>.</p>
              <p className="text-xs mt-2">The sandbox runs every rule against your context and explains why each rule did or didn't fire.</p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Summary card */}
              <div className={`rounded-lg shadow p-5 border-l-4 ${result.summary.blocking ? 'bg-red-50 border-red-500' : result.summary.total_required_approvals > 0 ? 'bg-amber-50 border-amber-500' : 'bg-green-50 border-green-500'}`} data-testid="sandbox-summary">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg">
                      {result.summary.blocking ? '🚫 Blocked' : result.summary.total_required_approvals > 0 ? '⚠️ Allowed with Approvals' : '✅ Allowed'}
                    </h3>
                    <p className="text-sm mt-1">
                      {result.summary.total_matched_rules} rule{result.summary.total_matched_rules !== 1 ? 's' : ''} fired
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    {Object.values(result.by_rule_type).reduce((acc, r) => acc + (r.total_rules_evaluated || 0), 0)} rules evaluated
                  </div>
                </div>

                {result.summary.required_approvals.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-300">
                    <p className="text-xs font-semibold text-amber-900 mb-1">Required Approvals:</p>
                    <div className="flex gap-2 flex-wrap">
                      {result.summary.required_approvals.map((a, i) => (
                        <span key={`${a.approver_role || 'a'}-${i}`} className="text-xs px-2 py-1 bg-amber-200 text-amber-900 rounded">
                          {a.approver_role || JSON.stringify(a)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {result.summary.matched_rules_overview.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-semibold mb-2">Matched Rules:</p>
                    <ul className="space-y-1">
                      {result.summary.matched_rules_overview.map((m, i) => (
                        <li key={`${m.rule_type}-${m.name}-${i}`} className="text-xs flex items-center gap-2">
                          <span className="px-1.5 py-0.5 bg-white border rounded">{m.rule_type}</span>
                          <span className="font-medium">{m.name}</span>
                          <span className="text-gray-500">→</span>
                          <span className={`px-1.5 py-0.5 rounded ${
                            m.action === 'disallow' ? 'bg-red-100 text-red-700' :
                            m.action === 'require_approval' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{m.action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Per-rule-type trace */}
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-semibold mb-3 text-sm">Detailed Trace by Rule Type</h3>
                <div className="space-y-3">
                  {Object.entries(result.by_rule_type).map(([type, r]) => (
                    <details key={type} className="border rounded p-3" data-testid={`sandbox-trace-${type}`}>
                      <summary className="cursor-pointer text-sm flex justify-between items-center">
                        <span className="font-semibold capitalize">{type.replace('_', ' ')}</span>
                        <span className="flex gap-2 items-center text-xs">
                          <span className="text-gray-500">{r.total_rules_evaluated || 0} rules</span>
                          <span className={`px-2 py-0.5 rounded ${
                            r.effective_action === 'disallow' ? 'bg-red-100 text-red-700' :
                            r.effective_action === 'require_approval' ? 'bg-amber-100 text-amber-700' :
                            r.matched_rules.length > 0 ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {r.effective_action}
                          </span>
                        </span>
                      </summary>
                      {r.trace && r.trace.length > 0 ? (
                        <div className="mt-3 space-y-2">
                          {r.trace.map((t) => (
                            <div key={t.rule_id} className={`p-2 rounded text-xs ${t.matched ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-semibold">{t.rule_name}</span>
                                <span className={`px-1.5 py-0.5 rounded ${t.matched ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                                  {t.matched ? 'MATCH' : 'SKIP'}
                                </span>
                              </div>
                              <p className="text-gray-600 italic">{t.reason}</p>
                              {t.scope_checks && t.scope_checks.some(s => !s.pass) && (
                                <div className="mt-2 pl-2 border-l-2 border-gray-300">
                                  <p className="font-medium text-gray-700">Scope filter blocked:</p>
                                  {t.scope_checks.filter(s => !s.pass).map((s) => (
                                    <p key={`${t.rule_id}-${s.scope}`} className="text-gray-600">• {s.scope.replace('scope_', '')}: {s.result}</p>
                                  ))}
                                </div>
                              )}
                              {t.condition_checks && t.condition_checks.length > 0 && (
                                <div className="mt-2 pl-2 border-l-2 border-gray-300">
                                  <p className="font-medium text-gray-700">Condition checks:</p>
                                  {t.condition_checks.map((c, j) => (
                                    <p key={`${t.rule_id}-cond-${j}`} className={c.pass ? 'text-green-700' : 'text-red-700'}>
                                      • {c.field} {c.operator} {JSON.stringify(c.expected)} → actual: {JSON.stringify(c.actual)} {c.pass ? '✓' : '✗'}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mt-2">No rules of this type exist.</p>
                      )}
                    </details>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSaveModal && result && (
        <SaveScenarioModal
          context={result.context}
          onClose={() => setShowSaveModal(false)}
          onSaved={() => { setShowSaveModal(false); loadScenarios(); }}
        />
      )}
    </div>
  );
}

function SaveScenarioModal({ context, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/rules/scenarios`, { name, description, context });
      onSaved();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b"><h2 className="text-lg font-semibold">Save Scenario</h2></div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. DE MD requisition" className="w-full px-3 py-2 border rounded" data-testid="scenario-name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description (optional)</label>
            <textarea rows="2" value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}



// ============================================================
// Interview Scheduler View
// ============================================================
export function InterviewSchedulerView() {
  const [interviews, setInterviews] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [filter, setFilter] = useState('upcoming');
  const [applications, setApplications] = useState([]);

  const loadInterviews = useCallback(async () => {
    try {
      const params = filter === 'upcoming' ? `?from_date=${new Date().toISOString()}` : '';
      const { data } = await axios.get(`${API}/cockpit/interviews${params}`);
      setInterviews(data);
    } catch (err) { logger.error(err); }
  }, [filter]);

  const loadApplications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cockpit/applications`);
      setApplications(data);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { loadInterviews(); }, [loadInterviews]);
  useEffect(() => { loadApplications(); }, [loadApplications]);

  const downloadICS = (interviewId) => {
    window.open(`${API}/cockpit/interviews/${interviewId}/ics`, '_blank');
  };

  const cancelInterview = async (id) => {
    if (!window.confirm('Cancel this interview? Attendees will need to be notified separately.')) return;
    await axios.delete(`${API}/cockpit/interviews/${id}`);
    loadInterviews();
  };

  const grouped = useMemo(() => interviews.reduce((acc, iv) => {
    const day = (iv.scheduled_time || '').slice(0, 10);
    if (!acc[day]) acc[day] = [];
    acc[day].push(iv);
    return acc;
  }, {}), [interviews]);

  return (
    <div data-testid="scheduler-view">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Interview Scheduler</h2>
          <p className="text-sm text-gray-500 mt-1">Schedule interviews with Teams, Zoom, Google Meet, phone, or in-person. Calendar invites work standalone via .ics download — no external account required.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAvailability(true)} className="px-3 py-2 bg-white border border-gray-300 text-sm rounded hover:bg-gray-50" data-testid="scheduler-availability-button">My Availability</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700" data-testid="scheduler-schedule-button">+ Schedule Interview</button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {['upcoming', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded text-sm ${filter === f ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300'}`}>{f === 'upcoming' ? 'Upcoming' : 'All'}</button>
        ))}
      </div>

      <div className="space-y-4">
        {Object.entries(grouped).sort().map(([day, items]) => (
          <div key={day}>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{new Date(day).toLocaleDateString(undefined, {weekday: 'long', month: 'short', day: 'numeric', year: 'numeric'})}</h3>
            <div className="space-y-2">
              {items.map(iv => (
                <div key={iv.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500" data-testid={`interview-card-${iv.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-semibold">{iv.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          iv.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          iv.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                          'bg-green-100 text-green-700'
                        }`}>{iv.status}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">{iv.platform}</span>
                        {iv.stage_name && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">{iv.stage_name}</span>}
                      </div>
                      <p className="text-sm text-gray-600">
                        {new Date(iv.scheduled_time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} · {iv.duration_minutes}min
                        {iv.candidate_name && <> · <strong>{iv.candidate_name}</strong></>}
                        {iv.requisition_title && <> · {iv.requisition_title}</>}
                      </p>
                      {iv.meeting_link && (
                        <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline truncate block mt-1" data-testid={`interview-link-${iv.id}`}>{iv.meeting_link}</a>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(iv.panel || []).map((p, idx) => (
                          <span key={`${p.email || 'p'}-${idx}`} className={`text-xs px-2 py-0.5 rounded ${p.required ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-500 italic'}`} title={p.role}>
                            {p.name || p.email} <span className="opacity-60">({p.role})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 ml-3">
                      <button onClick={() => downloadICS(iv.id)} className="text-xs px-2 py-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded" data-testid={`interview-ics-${iv.id}`}>📅 Download .ics</button>
                      {iv.status !== 'cancelled' && <button onClick={() => cancelInterview(iv.id)} className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded">Cancel</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {interviews.length === 0 && <div className="bg-white p-6 rounded-lg text-center text-gray-500">No interviews scheduled yet.</div>}
      </div>

      {showCreate && (
        <CreateInterviewModal applications={applications} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadInterviews(); }} />
      )}
      {showAvailability && (
        <AvailabilityModal onClose={() => setShowAvailability(false)} />
      )}
    </div>
  );
}

function CreateInterviewModal({ applications, onClose, onCreated }) {
  const [form, setForm] = useState({
    application_id: '',
    title: 'Interview',
    scheduled_time: '',
    duration_minutes: 60,
    location_type: 'teams',
    location_text: '',
    description: '',
    timezone_name: 'Europe/London',
    stage_name: ''
  });
  const [panel, setPanel] = useState([]);
  const [newPanelist, setNewPanelist] = useState({email: '', name: '', role: 'interviewer', required: true});

  const addPanelist = () => {
    if (!newPanelist.email) return;
    setPanel([...panel, newPanelist]);
    setNewPanelist({email: '', name: '', role: 'interviewer', required: true});
  };
  const removePanelist = (idx) => setPanel(panel.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const isoTime = new Date(form.scheduled_time).toISOString();
      await axios.post(`${API}/cockpit/interviews`, {...form, scheduled_time: isoTime, panel});
      onCreated();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-interview-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b"><h2 className="text-xl font-semibold">Schedule Interview</h2></div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Application</label>
            <select required value={form.application_id} onChange={e => setForm({...form, application_id: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="interview-application">
              <option value="">— select candidate × requisition —</option>
              {applications.map(a => (
                <option key={a.id} value={a.id}>{a.candidate_name || 'Unknown'} → {a.requisition_title} ({a.stage})</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Stage</label>
              <input value={form.stage_name} onChange={e => setForm({...form, stage_name: e.target.value})} placeholder="e.g. Round 1" className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">When</label>
              <input required type="datetime-local" value={form.scheduled_time} onChange={e => setForm({...form, scheduled_time: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="interview-time" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duration (min)</label>
              <input type="number" min="15" step="15" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Format</label>
              <select value={form.location_type} onChange={e => setForm({...form, location_type: e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="interview-format">
                <option value="teams">Microsoft Teams</option>
                <option value="zoom">Zoom</option>
                <option value="google_meet">Google Meet</option>
                <option value="phone">Phone</option>
                <option value="in_person">In Person</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{form.location_type === 'phone' ? 'Phone Number' : form.location_type === 'in_person' ? 'Address' : 'Override Link (optional)'}</label>
              <input value={form.location_text} onChange={e => setForm({...form, location_text: e.target.value})} className="w-full px-3 py-2 border rounded" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes for attendees</label>
            <textarea rows="2" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" />
          </div>

          <fieldset className="border p-3 rounded">
            <legend className="text-sm font-medium px-1">Panel & Notifications</legend>
            <p className="text-xs text-gray-500 mb-2">The candidate is added automatically. Add hiring manager, HR, and additional interviewers. Mark HR as <em>FYI</em> to keep them informed without requiring attendance.</p>
            <div className="space-y-1">
              {panel.map((p, idx) => (
                <div key={`${p.email || 'panelist'}-${idx}`} className="flex items-center gap-2 text-sm bg-gray-50 px-2 py-1 rounded">
                  <span className="flex-1">{p.name || p.email}</span>
                  <span className="text-xs px-1.5 py-0.5 bg-white border rounded">{p.role}</span>
                  {!p.required && <span className="text-xs italic text-gray-500">optional</span>}
                  <button type="button" onClick={() => removePanelist(idx)} className="text-xs px-1 text-red-600">×</button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-6 gap-1 mt-2">
              <input placeholder="email" value={newPanelist.email} onChange={e => setNewPanelist({...newPanelist, email: e.target.value})} className="col-span-2 px-2 py-1 border rounded text-sm" />
              <input placeholder="name" value={newPanelist.name} onChange={e => setNewPanelist({...newPanelist, name: e.target.value})} className="col-span-1 px-2 py-1 border rounded text-sm" />
              <select value={newPanelist.role} onChange={e => setNewPanelist({...newPanelist, role: e.target.value})} className="col-span-1 px-2 py-1 border rounded text-sm">
                <option value="interviewer">Interviewer</option>
                <option value="hiring_manager">Hiring Mgr</option>
                <option value="hr">HR</option>
                <option value="recruiter">Recruiter</option>
                <option value="fyi">FYI</option>
              </select>
              <label className="col-span-1 text-xs flex items-center gap-1">
                <input type="checkbox" checked={newPanelist.required} onChange={e => setNewPanelist({...newPanelist, required: e.target.checked})} /> Required
              </label>
              <button type="button" onClick={addPanelist} className="col-span-1 text-xs px-2 py-1 bg-indigo-600 text-white rounded">Add</button>
            </div>
          </fieldset>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded text-xs text-blue-900">
            💡 A meeting link will be auto-generated for {form.location_type}. All attendees receive a downloadable .ics file (works with Outlook, Google, Apple).
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm" data-testid="interview-submit">Schedule</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AvailabilityModal({ onClose }) {
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({start_time: '', end_time: '', notes: ''});

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/cockpit/availability?mine=true`);
    setSlots(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cockpit/availability`, {
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        notes: form.notes
      });
      setForm({start_time: '', end_time: '', notes: ''});
      load();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/cockpit/availability/${id}`);
    load();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">My Availability</h2>
          <p className="text-xs text-gray-500 mt-1">Mark times you're free to interview. Useful for shared visibility.</p>
        </div>
        <form onSubmit={add} className="p-5 space-y-2 border-b">
          <div className="grid grid-cols-2 gap-2">
            <input required type="datetime-local" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="px-2 py-1 border rounded text-sm" />
            <input required type="datetime-local" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} className="px-2 py-1 border rounded text-sm" />
          </div>
          <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-2 py-1 border rounded text-sm" />
          <button type="submit" className="px-3 py-1 bg-indigo-600 text-white rounded text-sm">+ Add Slot</button>
        </form>
        <div className="p-5 max-h-96 overflow-y-auto space-y-2">
          {slots.map(s => (
            <div key={s.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
              <div>
                <p>{new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleTimeString()}</p>
                {s.notes && <p className="text-xs text-gray-500">{s.notes}</p>}
              </div>
              <button onClick={() => remove(s.id)} className="text-xs text-red-600">×</button>
            </div>
          ))}
          {slots.length === 0 && <p className="text-sm text-gray-400 text-center">No availability slots yet.</p>}
        </div>
        <div className="p-3 border-t text-right">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Jobs Board — Recruiter publish view + Public job board
// ============================================================
export function PublishJobsView() {
  const [reqs, setReqs] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cockpit/requisitions`);
      setReqs(data);
    } catch (err) { logger.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="publish-jobs-view">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Jobs Board — Publishing</h2>
          <p className="text-sm text-gray-500 mt-1">Publish requisitions to the public careers page at <a href="/jobs" className="text-indigo-600 underline">/jobs</a>.</p>
        </div>
      </div>

      <div className="space-y-2">
        {reqs.map(r => (
          <div key={r.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500" data-testid={`publish-req-${r.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold">{r.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${r.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {r.published ? '🌐 Published' : 'Draft'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">{r.country} · {r.legal_entity}</span>
                  {r.business_line && <span className="text-xs px-2 py-0.5 rounded bg-teal-100 text-teal-700">{r.business_line}</span>}
                </div>
                {r.public_description && <p className="text-sm text-gray-600">{r.public_description.slice(0, 200)}{r.public_description.length > 200 ? '…' : ''}</p>}
                <div className="text-xs text-gray-500 mt-1 flex gap-3">
                  {r.salary_range && <span>💰 {r.salary_range}</span>}
                  {r.location_text && <span>📍 {r.location_text}</span>}
                  {r.remote_policy && <span>🏠 {r.remote_policy}</span>}
                  {r.posted_at && <span>📅 Posted {new Date(r.posted_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(r)} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded" data-testid={`publish-${r.published ? 'edit' : 'publish'}-${r.id}`}>{r.published ? 'Edit' : 'Publish'}</button>
                {r.published && <a href={`/jobs/${r.id}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded" data-testid={`publish-view-${r.id}`}>View ↗</a>}
              </div>
            </div>
          </div>
        ))}
        {reqs.length === 0 && <p className="text-sm text-gray-500 bg-white p-6 rounded-lg">No requisitions yet. Create one in the Vault Hub.</p>}
      </div>

      {editing && (
        <PublishJobModal req={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function PublishJobModal({ req, onClose, onSaved }) {
  const [form, setForm] = useState({
    published: req.published !== false,
    public_description: req.public_description || req.description || '',
    salary_range: req.salary_range || '',
    location_text: req.location_text || '',
    remote_policy: req.remote_policy || 'hybrid',
    employment_type: req.employment_type || 'permanent',
    apply_url: req.apply_url || ''
  });
  const [drafting, setDrafting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/cockpit/requisitions/${req.id}/publish`, form);
      onSaved();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const { data } = await axios.post(`${API}/cockpit/jd-draft`, {
        role_title: req.title,
        seniority: req.seniority,
        team: req.business_line,
        location: form.location_text || undefined,
        remote_policy: form.remote_policy,
        employment_type: form.employment_type,
        salary_range: form.salary_range || undefined,
      });
      setForm((f) => ({ ...f, public_description: data.markdown }));
    } catch (err) {
      alert('AI draft failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDrafting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b">
          <h2 className="text-xl font-semibold">{form.published ? 'Update' : 'Publish'} — {req.title}</h2>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} data-testid="publish-toggle" />
            <strong>Visible on public careers page</strong>
          </label>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium">Public Description</label>
              <button
                type="button"
                onClick={draftWithAI}
                disabled={drafting}
                data-testid="ai-jd-draft-btn"
                className="text-xs px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {drafting ? '✨ Drafting…' : '✨ Draft with AI'}
              </button>
            </div>
            <textarea required rows="8" value={form.public_description} onChange={e => setForm({...form, public_description: e.target.value})} className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="publish-description" />
            <p className="text-xs text-gray-500 mt-1">AI drafts use Claude Sonnet 4.5. Review before publishing.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Salary Range</label>
              <input value={form.salary_range} onChange={e => setForm({...form, salary_range: e.target.value})} placeholder="£90,000 - £120,000" className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input value={form.location_text} onChange={e => setForm({...form, location_text: e.target.value})} placeholder="London, UK" className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Remote Policy</label>
              <select value={form.remote_policy} onChange={e => setForm({...form, remote_policy: e.target.value})} className="w-full px-3 py-2 border rounded">
                <option value="onsite">Onsite</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Employment Type</label>
              <select value={form.employment_type} onChange={e => setForm({...form, employment_type: e.target.value})} className="w-full px-3 py-2 border rounded">
                <option value="permanent">Permanent</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">External Apply URL (optional)</label>
            <input value={form.apply_url} onChange={e => setForm({...form, apply_url: e.target.value})} placeholder="Leave blank to use internal apply flow" className="w-full px-3 py-2 border rounded" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm" data-testid="publish-save">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============================================================
// Public Jobs Board (no auth) + Job Detail page
// ============================================================
export function PublicJobsBoard() {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({country: '', remote_policy: '', business_line: ''});

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await axios.get(`${API}/jobs/public?${params}`);
      setJobs(data);
    } catch (err) { logger.error(err); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-jobs-board">
      <header className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">Careers at Vault Hire</h1>
          <p className="text-indigo-100">Join our world-class banking team. Apply with your candidate-owned vault — keep control of your data.</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3 items-center">
          <select value={filters.country} onChange={e => setFilters({...filters, country: e.target.value})} className="px-3 py-1.5 border rounded text-sm">
            <option value="">All countries</option>
            {['GB','US','DE','FR','ES','IT','NL','SG','HK','JP','AE','CH'].map(c => <option key={c}>{c}</option>)}
          </select>
          <select value={filters.remote_policy} onChange={e => setFilters({...filters, remote_policy: e.target.value})} className="px-3 py-1.5 border rounded text-sm">
            <option value="">All policies</option>
            <option value="onsite">Onsite</option>
            <option value="hybrid">Hybrid</option>
            <option value="remote">Remote</option>
          </select>
          <select value={filters.business_line} onChange={e => setFilters({...filters, business_line: e.target.value})} className="px-3 py-1.5 border rounded text-sm">
            <option value="">All business lines</option>
            <option>Investment Banking</option>
            <option>Markets</option>
            <option>Retail Banking</option>
            <option>Wealth Management</option>
            <option>Asset Management</option>
            <option>Corporate Banking</option>
            <option>Risk & Compliance</option>
            <option>Technology</option>
          </select>
          <span className="text-sm text-gray-500 ml-auto">{jobs.length} role{jobs.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map(j => (
            <a key={j.id} href={`/jobs/${j.id}`} className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition border border-gray-200 block" data-testid={`public-job-${j.id}`}>
              <h3 className="font-semibold text-lg text-gray-900">{j.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{j.legal_entity}</p>
              <div className="flex flex-wrap gap-1 mt-3">
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">📍 {j.location_text || j.country}</span>
                {j.remote_policy && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">{j.remote_policy}</span>}
                {j.business_line && <span className="text-xs px-2 py-0.5 rounded bg-teal-100 text-teal-700">{j.business_line}</span>}
                {j.seniority_band && <span className="text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">{j.seniority_band}</span>}
              </div>
              {j.salary_range && <p className="text-sm text-gray-700 mt-2">💰 {j.salary_range}</p>}
              {j.public_description && <p className="text-sm text-gray-500 mt-2 line-clamp-3">{j.public_description.slice(0, 140)}…</p>}
              <p className="text-xs text-indigo-600 mt-3">View details →</p>
            </a>
          ))}
          {jobs.length === 0 && <p className="col-span-3 text-center text-gray-500 py-12">No matching roles right now. Check back soon.</p>}
        </div>
      </main>
    </div>
  );
}

export function PublicJobDetail() {
  const [job, setJob] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [coverNote, setCoverNote] = useState('');
  const [authedUser, setAuthedUser] = useState(undefined); // undefined=loading, null=anon, object=user
  const jobId = window.location.pathname.split('/').pop();

  useEffect(() => {
    axios.get(`${API}/jobs/public/${jobId}`)
      .then(r => setJob(r.data))
      .catch(err => setLoadError(err.response?.data?.detail || err.message || 'Failed to load this job'));
    axios.get(`${API}/auth/me`).then(r => setAuthedUser(r.data)).catch(() => setAuthedUser(null));
  }, [jobId]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-lg shadow p-6 max-w-md text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Job not available</h2>
          <p className="text-sm text-gray-600">{loadError}</p>
          <a href="/jobs" className="inline-block mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded">← All jobs</a>
        </div>
      </div>
    );
  }
  if (!job) return <div className="p-8 text-center text-gray-500">Loading job…</div>;

  const apply = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/jobs/public/${jobId}/apply`, {cover_note: coverNote});
      setApplied(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) {
        if (window.confirm('Please log in to apply. Go to login page?')) {
          window.location.href = `/login?redirect=/jobs/${jobId}`;
        }
      } else {
        alert(detail || err.message);
      }
    } finally {
      setApplying(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-job-detail">
      <header className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <a href="/jobs" className="text-indigo-200 text-sm">← All jobs</a>
          <h1 className="text-3xl font-bold mt-2">{job.title}</h1>
          <p className="text-indigo-100 mt-1">{job.legal_entity}</p>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">📍 {job.location_text || job.country}</span>
            {job.remote_policy && <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">{job.remote_policy}</span>}
            {job.business_line && <span className="text-xs px-2 py-1 rounded bg-teal-100 text-teal-700">{job.business_line}</span>}
            {job.seniority_band && <span className="text-xs px-2 py-1 rounded bg-pink-100 text-pink-700">{job.seniority_band}</span>}
            {job.employment_type && <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{job.employment_type}</span>}
          </div>
          {job.salary_range && <p className="text-lg font-semibold text-gray-900 mb-3">💰 {job.salary_range}</p>}
          <div className="prose max-w-none whitespace-pre-wrap text-gray-800">{job.public_description || job.description}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold text-lg mb-3">Apply Now</h2>
          {applied ? (
            <div className="bg-green-50 border border-green-200 p-4 rounded text-green-800" data-testid="apply-success">
              ✅ Application submitted! The recruiter will be in touch via your Vault Hire account.
            </div>
          ) : job.apply_url ? (
            <a href={job.apply_url} target="_blank" rel="noreferrer" className="inline-block px-5 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Apply on partner site ↗</a>
          ) : authedUser === undefined ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : !authedUser ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Please log in or create a candidate account to apply.</p>
              <div className="flex gap-2">
                <a href={`/login?redirect=/jobs/${jobId}`} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm" data-testid="apply-login-link">Log in</a>
                <a href={`/register?role=candidate&redirect=/jobs/${jobId}`} className="px-4 py-2 bg-white border border-indigo-600 text-indigo-700 rounded hover:bg-indigo-50 text-sm" data-testid="apply-register-link">Create candidate account</a>
              </div>
            </div>
          ) : authedUser.role !== 'candidate' ? (
            <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded">You're signed in as <strong>{authedUser.role}</strong>. Only candidate accounts can apply.</p>
          ) : (
            <div className="space-y-3">
              <textarea rows="4" value={coverNote} onChange={e => setCoverNote(e.target.value)} placeholder="Add a short cover note (optional)" className="w-full px-3 py-2 border rounded text-sm" data-testid="cover-note" />
              <button onClick={apply} disabled={applying} className="px-5 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50" data-testid="apply-button">
                {applying ? 'Submitting…' : 'Apply with Vault'}
              </button>
              <p className="text-xs text-gray-500">You'll need a candidate account. Your vault stays under your control.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

